import React, { useState } from 'react';
import { BucketByFeatures, FeatureOptions } from '../../phonemes/bucketByFeatures';
import { IpaType } from "../../phonemes/ipa";

interface FeatureChartProps {
  /** symbols to include in chart if they match the desired set of features */
  readonly symbols: IpaType[];

  /** feature breakdown across columns */
  readonly columns: FeatureOptions[]; // e.g. placeOfArticulation
  /** feature breakdown across rows */
  readonly rows: FeatureOptions[];    // e.g. mannerOfArticulation
  /** feature breakdown within a table cell */
  readonly cells: FeatureOptions[];   // e.g. ["voiceless", "voiced"]

  /** default: true */
  readonly showColumnHeaders?: boolean;
  /** default: true */
  readonly showRowHeaders?: boolean;
  /** what to display when there was no symbol for the given features */
  readonly empty: IpaType;

  /** additional info applied to cells & symbols */
  readonly cellProps: CellProps;

  readonly rowStyle?: React.CSSProperties;
}

export interface CellProps {
  /** what to display for a given IPA string */
  readonly lookup?: (ipa: IpaType) => string;
  /** called when a symbol is clicked on */
  readonly clicked?: (ipa: IpaType) => void;
  /** true if a call to 'clicked' will do something useful */
  readonly canClick?: (ipa: IpaType) => boolean;
  /** called when the mouse is over a symbol */
  readonly hovered?: (ipa: IpaType) => void;
  /** called when a symbol is right-clicked on */
  readonly rightClicked?: (ipa: IpaType) => void;
  /** what background color to use for a given IPA string */
  readonly background?: (ipa: IpaType) => string | undefined;
  /** symbols to bold */
  readonly bold?: Set<IpaType>;
  /** add a <br/> between the two symbols in a cell */
  readonly br?: boolean;
}

/**
 * show an IPA chart organized by rows & columns of features, with cells organized by features as well
 */
export function FeatureChart(props: FeatureChartProps) {
  const [bucketer] = useState(() => new BucketByFeatures(props.symbols, props.columns, props.rows, props.cells));
  const [colLabels] = useState(props.columns.map(f => Array.isArray(f) ? f.join(' ') : f));
  const [rowLabels] = useState(props.rows.map(f => Array.isArray(f) ? f.join(' ') : f));
  const colHeaders = props.showColumnHeaders ?? true;
  const rowHeaders = props.showRowHeaders ?? true;

  return (<table>
    {colHeaders ? <ColumnHeaders emptyFirst={rowHeaders} labels={colLabels} /> : <></>}
    <tbody>
      {rowLabels.map((label, i) =>
        <SymbolRow label={rowHeaders ? label : undefined} bucketer={bucketer} empty={props.empty}
          key={i} row={i} columns={props.columns} cells={props.cells} cellProps={props.cellProps}
          rowStyle={props.rowStyle} />
      )}
    </tbody>
  </table>);
}

interface ColumnHeadersProps {
  readonly emptyFirst: boolean;
  readonly labels: string[];
  readonly rowStyle?: React.CSSProperties;
}
/** display column headers for the table */
function ColumnHeaders(props: ColumnHeadersProps) {
  return (<thead><tr>
    {props.emptyFirst ? <td /> : <></>}
    {props.labels.map(label => <th key={label}>{label}</th>)}
  </tr></thead>);
}

interface SymbolRowProps {
  readonly label?: string;
  readonly bucketer: BucketByFeatures;
  readonly row: number;
  readonly columns: FeatureOptions[];
  readonly cells: FeatureOptions[];
  readonly empty: IpaType;
  readonly cellProps: CellProps;
  readonly rowStyle?: React.CSSProperties;
}
/** display a row of symbols, optionally with a row label */
function SymbolRow(props: SymbolRowProps) {
  return (<tr style={props.rowStyle}>
    {props.label ? <th>{props.label}</th> : <></>}
    {props.columns.map((_, c) => <SymbolCell bucketer={props.bucketer} key={c}
      row={props.row} column={c} cells={props.cells} empty={props.empty} cellProps={props.cellProps} />)}
  </tr>);
}

interface SymbolCellProps {
  readonly bucketer: BucketByFeatures;
  readonly row: number;
  readonly column: number;
  readonly cells: FeatureOptions[];
  readonly empty: IpaType;
  readonly cellProps: CellProps;
}
/** fill a table cell with the symbols for (column,row) & the given cells */
function SymbolCell(props: SymbolCellProps) {
  let hasAny = false;
  const symbols = props.cells.map((_, cell) => {
    const symbol = props.bucketer.fetch(props.column, props.row, cell);
    if (symbol) hasAny = true;
    return symbol;
  });

  if (!hasAny) {
    return <td></td>;
  }

  const between = ' ';
  return <td>{symbols.map((s, i) => <span key={i}>
    <SingleSymbol prePad={i === 0 ? undefined : between} symbol={s} empty={props.empty} cellProps={props.cellProps} />
  </span>)}</td>;
}

interface SingleSymbolProps {
  readonly prePad?: string;   // show before symbol
  readonly symbol?: IpaType;  // symbol to show with optional props
  readonly empty: IpaType;    // if !symbol, show this instead
  readonly cellProps: CellProps;
}
/** a single symbol */
function SingleSymbol(props: SingleSymbolProps) {
  const symbol = props.symbol && props.cellProps.lookup
    ? props.cellProps.lookup(props.symbol)
    : props.symbol ?? props.empty;

  function over() {
    if (props.cellProps.hovered && props.symbol) {
      props.cellProps.hovered(props.symbol);
    }
  }
  function clicked() {
    if (props.cellProps.clicked && props.symbol) {
      props.cellProps.clicked(props.symbol);
    }
  }
  const canClick = props.cellProps.canClick && props.symbol
    ? props.cellProps.canClick(props.symbol)
    : props.cellProps.clicked !== undefined && props.symbol;
  const className = canClick ? 'pointer' : 'nopointer';

  function rightClick(e: RightClickEvent) {
    if (props.cellProps.rightClicked && props.symbol) {
      props.cellProps.rightClicked(props.symbol);
      e.preventDefault(); // stop the context menu from appearing
    }
  }

  const style = props.cellProps.background && props.symbol
    ? { backgroundColor: props.cellProps.background(props.symbol) }
    : undefined;
  const bold = props.cellProps.bold && props.symbol && props.cellProps.bold.has(props.symbol);
  const pad = props.prePad && props.cellProps.br
    ? <br />
    : props.prePad;

  const symbolSpan = (
    <span className={className} style={style} onClick={() => clicked()} onMouseOver={() => over()} onContextMenu={(e) => rightClick(e)}>
      {symbol}
    </span>);

  return (<>
    {pad}
    {bold ? <b>{symbolSpan}</b> : <>{symbolSpan}</>}
  </>);
}

interface RightClickEvent {
  preventDefault(): void;
}
